import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import moment, { Moment } from 'moment';
import { agent } from '@shared/services/agent';
import { selectConfig } from '@shared/store/config.duck';
import { selectCurrentUser } from '@shared/store/auth.duck';
import { isCompanyAdminUser, isOfficeAdminUser } from '@shared/utils/AccessControl';
import { DATE_FORMAT } from '@shared/utils/DateUtils';
import Colours from '@shared/ui/atoms/Colours';
import { OFFICE_OPTION, WhereaboutsOption } from '@features/whereabouts/services/WhereaboutOptions';

// A person's first automatic check-in on a day, in the office's local time
export interface CheckinConfirmation {
  userId: string;
  date: string;
  time: string;
  timeZone?: string | null;
  officeId?: number | null;
}

export type CheckinLookup = (userId: string | undefined, date: string) => CheckinConfirmation | undefined;

export const CheckinConfirmationsContext = createContext<CheckinLookup>(() => undefined);

export function useCheckinConfirmations(): CheckinLookup {
  return useContext(CheckinConfirmationsContext);
}

/**
 * Loads check-ins for the days in view when the company has "Mark bookings as confirmed" on and this user
 * may see them. The API applies the same rules, so this only saves a request that would come back empty.
 */
export function useLoadCheckinConfirmations(days: Moment[]): CheckinLookup {
  const config = useSelector(selectConfig);
  const user = useSelector(selectCurrentUser);
  const canSee = !!config?.checkinConfirmBookingsEnabled
    && (!!config?.checkinConfirmedVisibleToEveryone || isCompanyAdminUser(user) || isOfficeAdminUser(user));
  const from = days.length > 0 ? days[0].format(DATE_FORMAT) : '';
  const to = days.length > 0 ? days[days.length - 1].format(DATE_FORMAT) : '';
  const [confirmations, setConfirmations] = useState<CheckinConfirmation[]>([]);

  useEffect(() => {
    if (!canSee || !from || !to) {
      setConfirmations([]);
      return;
    }
    let cancelled = false;
    agent.requests.get(`/api/whereabouts/checkins?from=${from}&to=${to}`)
      .then((data: CheckinConfirmation[]) => { if (!cancelled) setConfirmations(data ?? []); })
      .catch(() => { if (!cancelled) setConfirmations([]); });
    return () => { cancelled = true; };
  }, [canSee, from, to]);

  return useMemo<CheckinLookup>(() => {
    const byPersonAndDay = new Map(confirmations.map(c => [`${c.userId}|${c.date}`, c]));
    return (userId, date) => userId ? byPersonAndDay.get(`${userId}|${date}`) : undefined;
  }, [confirmations]);
}

// Only days in the office can be confirmed
export function isOfficeDay(amOption?: WhereaboutsOption, pmOption?: WhereaboutsOption): boolean {
  return amOption?.id === OFFICE_OPTION.id || pmOption?.id === OFFICE_OPTION.id;
}

export function ConfirmedTick(props: { confirmations: CheckinConfirmation[], officeNameMap: Map<number, string> }) {
  const { confirmations, officeNameMap } = props;
  if (confirmations.length === 0) return null;

  const describe = (c: CheckinConfirmation) => {
    const officeName = c.officeId ? officeNameMap.get(c.officeId) : undefined;
    return `${c.time}${officeName ? ` at ${officeName}` : ''}`;
  };
  const label = confirmations.length === 1
    ? `Confirmed: checked in ${describe(confirmations[0])}`
    : `Confirmed: checked in ${confirmations.map(c => `${moment(c.date).format('ddd')} ${describe(c)}`).join(', ')}`;

  return (
    <TickBadge title={label} role="img" aria-label={label}>
      <svg viewBox="0 0 16 16" width={16} height={16} aria-hidden="true">
        <circle cx="8" cy="8" r="8" fill={Colours.green} />
        <path d="M4.5 8.2 7 10.6l4.5-5" fill="none" stroke={Colours.white} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </TickBadge>
  );
}

// Holds the confirmed tick and the note icon together at the end of a cell
export const CellBadges = styled.div`
  margin-left: auto;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const TickBadge = styled.span`
  display: inline-flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;
